/* ThunderParts storefront interactions. No mock data, cart or localization emulation. */
(() => {
  const mounted = new WeakMap();
  function mount(scope = document) {
    const roots = [...scope.querySelectorAll('.tp-root')];
    if (scope.matches?.('.tp-root')) roots.unshift(scope);
    roots.forEach(root => {
      if (mounted.has(root)) return;
      const controller = new AbortController();
      mounted.set(root, controller);
      const on = (node, event, fn, options = {}) => node?.addEventListener(event, fn, { ...options, signal: controller.signal });
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
      const dialog = root.querySelector('[data-tp-locale]');
      on(root.querySelector('[data-tp-locale-open]'), 'click', () => dialog?.showModal());
      on(root.querySelector('[data-tp-locale-close]'), 'click', () => dialog?.close());
      on(dialog, 'click', e => { if (e.target === dialog) { const r = dialog.getBoundingClientRect(); if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dialog.close(); } });
      const searchToggle = root.querySelector('[data-tp-search-toggle]');
      on(searchToggle, 'click', () => {
        const panel = root.querySelector('.tp-search');
        panel.hidden = !panel.hidden;
        searchToggle.setAttribute('aria-expanded', String(!panel.hidden));
        if (!panel.hidden) panel.querySelector('input[type=search]').focus();
      });
      on(root.querySelector('[data-tp-ticker]'), 'click', e => {
        const button = e.currentTarget;
        const paused = button.getAttribute('aria-pressed') !== 'true';
        button.setAttribute('aria-pressed', String(paused));
        root.querySelector('.ticker-track').style.animationPlayState = paused ? 'paused' : 'running';
        button.textContent = paused ? '▷' : 'Ⅱ';
      });
      const rail = root.querySelector('[data-tp-reviews]');
      if (rail) {
        const cards = [...rail.children];
        const dots = [...root.querySelectorAll('[data-tp-review-dot]')];
        const pause = root.querySelector('[data-tp-review-pause]');
        const mobile = window.matchMedia('(max-width: 760px)');
        let current = 0, timer, paused = false, hovered = false, focused = false, touching = false, visible = false;
        const stop = () => { clearInterval(timer); timer = undefined; };
        const show = index => {
          current = (index + cards.length) % cards.length;
          rail.scrollTo({ left: cards[current].offsetLeft - cards[0].offsetLeft, behavior: reduce.matches ? 'instant' : 'smooth' });
        };
        const run = () => {
          stop();
          if (cards.length > 1 && mobile.matches && visible && !paused && !hovered && !focused && !touching && !document.hidden && !reduce.matches && !window.Shopify?.designMode) timer = setInterval(() => show(current + 1), 6500);
        };
        dots.forEach((dot, i) => on(dot, 'click', () => { show(i); run(); }));
        on(rail, 'scroll', () => {
          const width = cards[0]?.getBoundingClientRect().width || 1;
          const gap = parseFloat(getComputedStyle(rail).gap) || 0;
          current = Math.max(0, Math.min(cards.length - 1, Math.round(rail.scrollLeft / (width + gap))));
          dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === current)));
        }, { passive: true });
        on(pause, 'click', () => { paused = !paused; pause.setAttribute('aria-pressed', String(paused)); pause.textContent = paused ? '▷' : 'Ⅱ'; run(); });
        const reviews = root.querySelector('.reviews');
        on(reviews, 'pointerenter', e => { if (e.pointerType === 'mouse') { hovered = true; run(); } });
        on(reviews, 'pointerleave', () => { hovered = false; run(); });
        on(reviews, 'focusin', () => { focused = true; run(); });
        on(reviews, 'focusout', e => { focused = reviews.contains(e.relatedTarget); run(); });
        on(rail, 'pointerdown', () => { touching = true; run(); });
        on(window, 'pointerup', () => { touching = false; run(); });
        on(window, 'pointercancel', () => { touching = false; run(); });
        on(document, 'visibilitychange', run);
        on(mobile, 'change', () => { show(0); run(); });
        on(reduce, 'change', run);
        const observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; run(); }, { threshold: .25 });
        observer.observe(rail);
        controller.signal.addEventListener('abort', () => { stop(); observer.disconnect(); }, { once: true });
      }

      const slider = root.querySelector('[data-tp-slider]');
      if (slider) {
        const slides = [...slider.querySelectorAll('[data-tp-slide]')];
        const dots = [...slider.querySelectorAll('[data-tp-dot]')];
        let current = 0, timer, paused = false, hovered = false, focused = false, start;
        const stop = () => { clearInterval(timer); timer = undefined; };
        const show = n => {
          current = (n + slides.length) % slides.length;
          slides.forEach((slide, i) => { slide.hidden = i !== current; slide.inert = i !== current; });
          dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === current)));
        };
        const run = () => {
          stop();
          if (slides.length > 1 && slider.dataset.autoplay === 'true' && !paused && !hovered && !focused && !document.hidden && !reduce.matches && !window.Shopify?.designMode) timer = setInterval(() => show(current + 1), Number(slider.dataset.interval) || 9000);
        };
        const navigate = n => { show(n); run(); };
        dots.forEach((dot, i) => on(dot, 'click', () => navigate(i)));
        on(slider.querySelector('[data-tp-prev]'), 'click', () => navigate(current - 1));
        on(slider.querySelector('[data-tp-next]'), 'click', () => navigate(current + 1));
        on(slider.querySelector('[data-tp-pause]'), 'click', e => { paused = !paused; e.currentTarget.setAttribute('aria-pressed', String(paused)); e.currentTarget.textContent = paused ? '▷' : 'Ⅱ'; run(); });
        on(slider, 'pointerenter', () => { hovered = true; run(); });
        on(slider, 'pointerleave', () => { hovered = false; run(); });
        on(slider, 'focusin', () => { focused = true; run(); });
        on(slider, 'focusout', e => { if (!slider.contains(e.relatedTarget)) { focused = false; run(); } });
        on(document, 'visibilitychange', run);
        on(reduce, 'change', run);
        on(slider, 'pointerdown', e => { if (e.pointerType !== 'mouse') start = [e.clientX, e.clientY]; });
        on(slider, 'pointerup', e => {
          if (!start || slides.length < 2) return;
          const dx = e.clientX - start[0], dy = e.clientY - start[1]; start = undefined;
          if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.4) navigate(current + (dx < 0 ? 1 : -1));
        });
        on(document, 'shopify:block:select', e => {
          const index = slides.findIndex(slide => slide === e.target || slide.contains(e.target));
          if (index >= 0) { paused = true; show(index); run(); }
        });
        controller.signal.addEventListener('abort', stop, { once: true });
        run();
      }
      const product = root.querySelector('[data-tp-product]');
      if (product) {
        const data = JSON.parse(product.querySelector('[data-tp-variants]').textContent);
        const select = product.querySelector('[name=id]');
        const optionButtons = [...product.querySelectorAll('[data-tp-option]')];
        if (optionButtons.length) { product.classList.add('tp-variant-enhanced'); select.classList.add('tp-select-accessible'); }
        optionButtons.forEach(button => on(button, 'click', () => { select.value = button.dataset.tpOption; select.dispatchEvent(new Event('change', { bubbles: true })); }));
        const media = [...product.querySelectorAll('[data-tp-media]')];
        const thumbs = [...product.querySelectorAll('[data-tp-thumb]')];
        const showMedia = id => {
          if (!media.some(item => item.dataset.tpMedia === String(id))) return;
          media.forEach(item => { item.hidden = item.dataset.tpMedia !== String(id); if (item.hidden) item.querySelectorAll('video').forEach(video => video.pause()); });
          thumbs.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.tpThumb === String(id))));
        };
        thumbs.forEach(button => on(button, 'click', () => showMedia(button.dataset.tpThumb)));
        const update = () => {
          const variant = data.find(v => String(v.id) === select.value);
          const canBuy = Boolean(variant?.available);
          product.querySelectorAll('[data-tp-add]').forEach(button => { button.disabled = !canBuy; button.textContent = !variant ? product.dataset.choose : canBuy ? product.dataset.add : product.dataset.soldOut; });
          optionButtons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.tpOption === select.value)));
          if (!variant) return;
          const quantity = product.querySelector('[name=quantity]');
          quantity.min = variant.quantity_min || 1; quantity.step = variant.quantity_step || 1;
          if (variant.quantity_max) quantity.max = variant.quantity_max; else quantity.removeAttribute('max');
          if (Number(quantity.value) < Number(quantity.min)) quantity.value = quantity.min;
          product.querySelectorAll('[data-tp-price]').forEach(price => { price.textContent = variant.money; });
          const compare = product.querySelector('[data-tp-compare]');
          compare.hidden = !variant.compare;
          compare.textContent = variant.compare || '';
          product.querySelector('[data-tp-availability]').textContent = variant.available ? '' : product.dataset.soldOut;
          if (variant.media) showMedia(variant.media);
          const url = new URL(location.href); url.searchParams.set('variant', variant.id); history.replaceState({}, '', url);
        };
        on(select, 'change', update);
        const chosen = select.value;
        if (chosen) update();
        else product.querySelectorAll('[data-tp-add]').forEach(button => { button.disabled = true; button.textContent = product.dataset.choose; });
        const zoom = product.querySelector('[data-tp-zoom]');
        product.querySelectorAll('[data-tp-zoom-open]').forEach(button => on(button, 'click', () => { zoom.querySelector('img').src = button.dataset.tpZoomOpen; zoom.showModal(); }));
        on(zoom?.querySelector('button'), 'click', () => zoom.close());
      }
    });
  }
  mount();
  document.addEventListener('shopify:section:load', e => mount(e.target));
  document.addEventListener('shopify:section:unload', e => e.target.querySelectorAll('.tp-root').forEach(root => { mounted.get(root)?.abort(); mounted.delete(root); }));
})();
